import { apiFetch } from './client'
import { ApiError } from './errors'
import { newRequestId } from './requestId'
import type { DescriptionBlock } from './jobDescription'

/**
 * s59 -- painel de IA do editor (ADR-0027 D3-D9; referencia Attio, S31-7).
 *
 * Contratos (lidos na DEFINICAO, s58):
 *   POST /api/ai/job-description-suggestions  {description_id, block}
 *        -> {status, suggestions[{text, violations[], trail_item_id}], round_id, redaction_counts, ...}
 *        O corpo leva SO referencias: o servidor le o cargo e o rascunho do banco (D5) e limpa dado pessoal (D9).
 *        403 code ai_services_terms_not_accepted | ai_services_budget_not_configured = IA nao habilitada.
 *        429 code ai_user_budget_exceeded = teto do usuario (D7); outro 429 = teto da empresa (D4).
 *   POST /api/draft-rounds/{round}/items/{item}/decision  {decision, final_text?}  (approved | edited | rejected)
 *
 * O que NAO entra no tipo: custo, tokens, modelo e versao do prompt (dado de operacao, nao de tela).
 */

export const SUGGESTABLE_BLOCKS: readonly DescriptionBlock[] = ['papel', 'entregas', 'detalhamento', 'conhecimentos']

export type SuggestionStatus = 'success' | 'rejected_by_validation' | 'degraded'

export interface Suggestion {
  text: string
  trailItemId: string
  /** Avisos do metodo (V1-V7) sobre ESTA sugestao: consultivos (D6), a pessoa decide. */
  warnings: string[]
}

export interface SuggestionResult {
  status: SuggestionStatus
  roundId: string | null
  suggestions: Suggestion[]
  /** Quantos dados pessoais foram trocados por marcadores antes de o texto sair (D9; aviso da tela). */
  redactedCount: number
  /** O X-Request-ID DESTE pedido: o "degraded" chega com HTTP 200 e mesmo assim precisa ser rastreavel (E5-s56). */
  requestId: string
}

interface SuggestionResponse {
  status: SuggestionStatus
  round_id: string | null
  redaction_counts: Record<string, number> | null
  suggestions: { text: string; trail_item_id?: string | null; violations: { message: string }[] }[]
}

export async function requestSuggestions(descriptionId: string, block: DescriptionBlock): Promise<SuggestionResult> {
  const requestId = newRequestId()
  const r = await apiFetch<SuggestionResponse>('/api/ai/job-description-suggestions', {
    method: 'POST',
    body: { description_id: descriptionId, block },
    requestId,
  })
  const suggestions = (r.suggestions ?? [])
    .filter((s) => typeof s.trail_item_id === 'string' && s.trail_item_id !== '')
    .map((s) => ({ text: s.text, trailItemId: s.trail_item_id as string, warnings: (s.violations ?? []).map((v) => v.message) }))
  const redactedCount = Object.values(r.redaction_counts ?? {}).reduce((a, n) => a + (Number(n) || 0), 0)
  return { status: r.status, roundId: r.round_id, suggestions, redactedCount, requestId }
}

export type TrailDecision = 'approved' | 'edited' | 'rejected'

export async function decideTrailItem(roundId: string, trailItemId: string, decision: TrailDecision, finalText?: string): Promise<void> {
  const body: Record<string, string> = { decision }
  if (decision === 'edited' && finalText !== undefined) body.final_text = finalText
  await apiFetch<unknown>(
    `/api/draft-rounds/${encodeURIComponent(roundId)}/items/${encodeURIComponent(trailItemId)}/decision`,
    { method: 'POST', body },
  )
}

export type AiErrorKind = 'not_enabled' | 'user_budget' | 'tenant_budget' | 'other'

const NOT_ENABLED_CODES = new Set(['ai_services_terms_not_accepted', 'ai_services_budget_not_configured'])

/** Classifica o erro da IA para a tela dizer O QUE aconteceu (e nao "erro inesperado" para uma regra conhecida). */
export function aiErrorKind(e: unknown): AiErrorKind {
  if (!(e instanceof ApiError)) return 'other'
  const d = e.detail
  const code = typeof d === 'object' && d !== null ? (d as { code?: unknown }).code : undefined
  if (e.status === 403 && typeof code === 'string' && NOT_ENABLED_CODES.has(code)) return 'not_enabled'
  if (e.status === 429) return code === 'ai_user_budget_exceeded' ? 'user_budget' : 'tenant_budget'
  return 'other'
}
